<template>
  <div>
    <a-card :bordered="false" title="期末处理（结转 · 结账 · 反结账）">
      <a-tabs v-model="activeTab" @change="onTabChange">
        <!-- ==================== 期末结转向导 ==================== -->
        <a-tab-pane tab="期末结转" key="checkout">
          <a-steps :current="step" style="margin-bottom: 24px;">
            <a-step title="选择年月" />
            <a-step title="结转处理" />
            <a-step title="结账检查" />
          </a-steps>

          <!-- Step 1 年月选择 -->
          <div v-if="step === 0">
            <a-form layout="inline" style="margin-bottom: 16px;">
              <a-form-item label="结账期间">
                <a-month-picker v-model="periodMoment" format="YYYY-MM" style="width:160px" />
              </a-form-item>
              <a-button type="primary" @click="goStep2">下一步</a-button>
            </a-form>
            <a-descriptions title="结账状态" :column="2" size="small" bordered style="max-width:640px;">
              <a-descriptions-item label="启用月份">{{ status.startYearMonth || '—' }}</a-descriptions-item>
              <a-descriptions-item label="当前期间">{{ status.currentYearMonth || '—' }}</a-descriptions-item>
              <a-descriptions-item label="已结账至">{{ status.closedThrough || '未结账' }}</a-descriptions-item>
              <a-descriptions-item label="可结账月份"><span style="color:#1890ff;">{{ status.nextCloseable || '—' }}</span></a-descriptions-item>
              <a-descriptions-item label="已结账月份" :span="2">{{ (status.closedMonths && status.closedMonths.length) ? status.closedMonths.join('、') : '无' }}</a-descriptions-item>
            </a-descriptions>
          </div>

          <!-- Step 2 结转卡片（统一交互：点卡片开预填凭证弹窗；行仅暂存内存，结账才落库） -->
          <div v-if="step === 1">
            <a-alert type="info" show-icon style="margin-bottom:12px;"
                     message="凭证行只暂存在页面内存中，点最后一步「结账」才一次性生成并审核；中途上一步/关闭页面不产生任何数据。" />
            <div style="margin-bottom: 12px;">
              <span>结账期间：<b>{{ period }}</b></span>
              <span style="margin-left:24px;">所得税率档：
                <a-radio-group v-model="rateKey" @change="loadCards">
                  <a-radio value="standard">标准 25%</a-radio>
                  <a-radio value="small">小微 5%</a-radio>
                </a-radio-group>
              </span>
            </div>
            <a-row :gutter="12" type="flex">
              <a-col v-for="c in cards" :key="c.key" :md="6" :sm="24" style="margin-bottom: 12px;">
                <a-card size="small" :class="{ 'gl-card-disabled': !c.enabled }">
                  <template slot="title">
                    {{ c.name }}
                    <a-tag v-if="c.type === 'profit'" color="blue">结账时自动</a-tag>
                    <a-tag v-else-if="configuredCards[c.key]" color="green">已暂存</a-tag>
                    <a-tag v-else-if="isAutoCard(c)" color="blue">自动测算</a-tag>
                    <a-tag v-else color="orange">手工填写</a-tag>
                  </template>
                  <div style="height:48px;overflow:hidden;">{{ c.reason }}</div>
                  <div style="margin:4px 0;">
                    <span style="font-size:18px;font-weight:600;">{{ fmtAmount(c.amount) }}</span>
                  </div>
                  <div style="color:#999;font-size:12px;height:18px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;">{{ (c.subjects || []).filter(Boolean).join(' / ') }}</div>
                  <div style="margin-top:8px;height:24px;">
                    <a-button v-if="c.type === 'profit'" size="small" @click="openCard(c)">查看凭证</a-button>
                    <a-button v-else size="small" type="primary" @click="openCard(c)">{{ configuredCards[c.key] ? '修改' : '暂存凭证' }}</a-button>
                  </div>
                </a-card>
              </a-col>
            </a-row>
            <a-button style="margin-right:8px;" @click="step = 0">上一步</a-button>
            <a-button type="primary" @click="goStep3">下一步</a-button>
          </div>

          <!-- Step 3 检查清单 + 结账 -->
          <div v-if="step === 2">
            <a-list size="small" bordered :loading="checking" :data-source="checks" style="max-width:640px;">
              <a-list-item slot="renderItem" slot-scope="c">
                <a-icon :type="c.pass ? 'check-circle' : 'close-circle'" :theme="c.pass ? 'twoTone' : 'filled'" :two-tone-color="c.pass ? '#52c41a' : '#f5222d'" style="margin-right:8px;" />
                <b>{{ c.title }}</b>：{{ c.detail }}
              </a-list-item>
            </a-list>
            <div style="margin-top:16px;">
              <a-button style="margin-right:8px;" @click="step = 1">上一步</a-button>
              <a-button type="primary" :loading="closing" :disabled="!allChecksPassed" @click="doClose">{{ period && period.endsWith('-12') ? '年结（结账）' : '结账' }}</a-button>
            </div>
            <a-result v-if="closeResult" style="max-width:640px;" :status="closeResult.success ? 'success' : 'error'" :title="closeResult.message">
              <template slot="extra">
                <span v-if="closeResult.cardCount">结转卡片凭证 {{ closeResult.cardCount }} 张　</span>
                <span v-if="closeResult.carryVoucherId">损益结转凭证 #{{ closeResult.carryVoucherId }}　</span>
                <span v-if="closeResult.profitVoucherId">利润分配凭证 #{{ closeResult.profitVoucherId }}</span>
              </template>
            </a-result>
          </div>
        </a-tab-pane>

        <!-- ==================== 反结账 ==================== -->
        <a-tab-pane tab="反结账" key="unclose">
          <a-form layout="inline" style="margin-bottom:16px;">
            <a-form-item label="选择已结账月份">
              <a-select v-model="unclosePeriod" style="width:160px" placeholder="选择月份">
                <a-select-option v-for="m in (status.closedMonths || [])" :key="m" :value="m">{{ m }}</a-select-option>
              </a-select>
            </a-form-item>
            <a-button type="danger" :loading="unclosing" @click="doUnclose">反结账</a-button>
          </a-form>
          <a-alert type="warning" show-icon message="反结账规则：仅允许反结账最后一个已结账月份；执行时删除该期结转凭证、解锁期间并回退账套当前期间，其后未结账月份的凭证保留不动。" />
        </a-tab-pane>
      </a-tabs>
    </a-card>

    <!-- 凭证编辑器（结转卡片复用标准新增/查看凭证界面；内存模式：保存=暂存行，不落库） -->
    <voucher-editor
      :visible="editorVisible"
      :mode="editorMode"
      :account-set-id="accountSetId"
      :period="period"
      :memory-mode="true"
      :prefill-items="editorPrefill"
      @update:visible="v => editorVisible = v"
      @confirm="onCardConfirm"
    />
  </div>
</template>

<script>
import moment from 'moment'
import VoucherEditor from './VoucherEditor'
import { closingStatus, closingPreCheck, closingClose, closingReverse, closingCarryPreview, accountSetCurrent } from './api'

const fmt2 = v => (v === null || v === undefined || v === '' || isNaN(Number(v))) ? '' : Number(v).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export default {
  name: 'CheckOutList',
  components: { VoucherEditor },
  data() {
    return {
      activeTab: 'checkout', step: 0,
      accountSetId: null,
      status: {}, periodMoment: null, period: '',
      rateKey: 'standard', cards: [],
      checks: [], checking: false, closing: false, closeResult: null,
      unclosePeriod: null, unclosing: false,
      editorVisible: false, editorMode: 'add', editorPrefill: null, activeCard: null,
      configuredCards: {}
    }
  },
  computed: {
    allChecksPassed() {
      return this.checks.length > 0 && this.checks.every(c => c.pass)
    }
  },
  created() { this.loadCurrent() },
  methods: {
    loadCurrent() {
      accountSetCurrent({}).then((res) => {
        if (res && res.success && res.data && res.data.id) {
          this.accountSetId = res.data.id
          this.loadStatus()
        } else { this.$message.warning('尚未建账') }
      })
    },
    loadStatus() {
      closingStatus({ accountSetId: this.accountSetId }).then((res) => {
        if (res && res.success && res.data) {
          this.status = res.data
          if (res.data.nextCloseable) this.periodMoment = moment(res.data.nextCloseable, 'YYYY-MM')
        }
      })
    },
    onTabChange() {
      if (this.activeTab === 'unclose') {
        const months = this.status.closedMonths || []
        this.unclosePeriod = months.length ? months[months.length - 1] : null
      }
    },
    // Step1 → Step2：校验期间并加载结转卡片
    goStep2() {
      if (!this.periodMoment) { this.$message.warning('请选择结账期间'); return }
      this.period = this.periodMoment.format('YYYY-MM')
      this.step = 1
      this.loadCards()
    },
    loadCards() {
      this.cards = []
      this.configuredCards = {}   // 切期间/税率档重置暂存（此前未落库，直接丢弃）
      closingCarryPreview({ accountSetId: this.accountSetId, period: this.period, rateKey: this.rateKey }).then((res) => {
        if (res && res.success && res.data) this.cards = res.data
        else this.$message.warning((res && res.message) || '卡片测算失败')
      })
    },
    // 是否自动测算卡（行含测算键）
    isAutoCard(c) {
      if (c.type === 'profit') return true
      return (c.lines || []).some(l => l.calcKey)
    },
    // 卡片测算行 → 凭证编辑器预填行（方向+金额 → 借/贷金额，金额≤0 置空）
    toPrefill(lines) {
      return (lines || []).map(l => ({
        subjectId: l.subjectId,
        summary: l.summary,
        debitAmount: l.direction === '借' && Number(l.amount) > 0 ? Number(l.amount) : undefined,
        creditAmount: l.direction === '贷' && Number(l.amount) > 0 ? Number(l.amount) : undefined
      }))
    },
    // 打开凭证编辑器：结转损益=只读查看服务端将生成的分录；其余卡=新增凭证界面（预填测算行/已暂存行）
    openCard(card) {
      if (card.type === 'profit') {
        if (!(card.lines || []).length) { this.$message.info('本期损益科目无余额，结账时不生成结转凭证'); return }
        this.editorMode = 'view'
        this.editorPrefill = this.toPrefill(card.lines)
      } else {
        this.editorMode = 'add'
        const stored = this.configuredCards[card.key]
        this.editorPrefill = stored ? stored.map(l => ({ ...l })) : this.toPrefill(card.lines)
      }
      this.activeCard = card
      this.editorVisible = true
    },
    // 编辑器内存模式保存回调：暂存分录行到页面内存（不落库；点「结账」时随 close 请求一次性提交）
    onCardConfirm(payload) {
      const card = this.activeCard
      if (!card || !payload || !payload.items) return
      this.configuredCards = { ...this.configuredCards, [card.key]: payload.items }
      card.amount = payload.items.reduce((s, l) => s + (Number(l.debitAmount) || 0), 0)
      this.$message.success('已暂存凭证行，点「结账」时才生成凭证')
    },
    // Step2 → Step3：加载检查清单
    goStep3() {
      this.step = 2
      this.loadChecks()
    },
    loadChecks() {
      this.checking = true
      this.closeResult = null
      closingPreCheck({ accountSetId: this.accountSetId, period: this.period }).then((res) => {
        if (res && res.success && res.data) this.checks = res.data
        else this.$message.warning((res && res.message) || '检查失败')
        this.checking = false
      }).catch(() => { this.checking = false })
    },
    doClose() {
      this.closing = true
      const cards = Object.keys(this.configuredCards).map(k => ({ cardKey: k, lines: this.configuredCards[k] }))
      closingClose({ accountSetId: this.accountSetId, period: this.period, cards }).then((res) => {
        if (res && res.success) {
          this.closeResult = {
            success: true, message: res.message || '结账成功',
            cardCount: res.data && res.data.cardVoucherIds ? res.data.cardVoucherIds.length : 0,
            carryVoucherId: res.data && res.data.carryVoucherId,
            profitVoucherId: res.data && res.data.profitVoucherId
          }
          this.configuredCards = {}
          this.loadStatus()
        } else {
          this.closeResult = { success: false, message: res.message || '结账失败' }
        }
        this.closing = false
      }).catch(() => { this.closing = false })
    },
    doUnclose() {
      if (!this.unclosePeriod) { this.$message.warning('请选择要反结账的月份'); return }
      this.$confirm({
        title: '确认反结账 ' + this.unclosePeriod + '？',
        content: '将删除该期结转凭证、解锁期间并回退账套当前期间；其后月份已有凭证将保留不动。',
        onOk: () => {
          this.unclosing = true
          return closingReverse({ accountSetId: this.accountSetId }).then((res) => {
            if (res && res.success) { this.$message.success(res.message || '反结账成功'); this.loadStatus(); this.unclosePeriod = null }
            else { this.$message.error((res && res.message) || '反结账失败') }
            this.unclosing = false
          }).catch(() => { this.unclosing = false })
        }
      })
    },
    fmtAmount(v) { return fmt2(v) }
  }
}
</script>

<style scoped>
.gl-card-disabled /deep/ .ant-card-head-title { color: #999; }
</style>
